import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, Building2, LogOut, Users, Building, ClipboardCheck, ArrowRightLeft, FileCode2, Rocket, Rabbit, HardDrive, BarChart3, FileSignature, Network, Bot } from 'lucide-react';
import { clearToken } from '../../services/api';
import { cn } from '../../lib/utils';
import type { ControlCenterFeature } from '../../models';
import { useFeatureFlags } from '../../features/FeatureFlags';
import controlCenterLogo from '../../assets/control-center-logo.png';

const nav: { group: string; path: string; label: string; icon: typeof LayoutGrid; feature?: ControlCenterFeature }[] = [
  { group: 'Операции', path: '/', label: 'Обзор', icon: LayoutGrid },
  { group: 'Операции', path: '/onboarding', label: 'Новый клиент', icon: Rocket, feature: 'onboarding' },
  { group: 'Операции', path: '/medical-checkups', label: 'Медосмотры', icon: ClipboardCheck, feature: 'medicalCheckups' },
  { group: 'Операции', path: '/queue-settings', label: 'Маршрутизация', icon: ArrowRightLeft, feature: 'queueSettings' },
  { group: 'Операции', path: '/document-templates', label: 'Шаблоны', icon: FileCode2, feature: 'documentTemplates' },
  { group: 'Операции', path: '/epl-integrations', label: 'ЭПЛ / ЭДО', icon: FileSignature, feature: 'eplIntegrations' },
  { group: 'Управление', path: '/tenants', label: 'Организации', icon: Building2, feature: 'tenants' },
  { group: 'Управление', path: '/users', label: 'Пользователи', icon: Users, feature: 'users' },
  { group: 'Управление', path: '/companies', label: 'Клиенты ЛК', icon: Building, feature: 'companies' },
  { group: 'Инфраструктура', path: '/rabbitmq', label: 'RabbitMQ', icon: Rabbit, feature: 'rabbitmq' },
  { group: 'Инфраструктура', path: '/meshcentral', label: 'MeshCentral', icon: Network, feature: 'meshcentral' },
  { group: 'Инфраструктура', path: '/ai-assistant', label: 'ИИ-помощник', icon: Bot },
  { group: 'Инфраструктура', path: '/s3-storage', label: 'S3 / MinIO', icon: HardDrive, feature: 's3Storage' },
  { group: 'Аналитика', path: '/analytics', label: 'Аналитика', icon: BarChart3, feature: 'analytics' },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isEnabled } = useFeatureFlags();
  const visibleNav = nav.filter((item) => !item.feature || isEnabled(item.feature));

  const logout = () => {
    clearToken();
    navigate('/login');
  };

  return (
    <>
      {open && <button className="fixed inset-0 z-30 bg-background/70 backdrop-blur-sm lg:hidden" onClick={onClose} aria-label="Закрыть меню" />}
      <aside className={cn(
        'fixed inset-y-0 left-0 z-40 flex h-screen w-[250px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar shadow-sm transition-transform lg:relative lg:z-auto lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full'
      )}>
      <div className="flex min-h-[84px] items-center px-5 py-3">
        <img
          src={controlCenterLogo}
          alt="Control Center"
          className="h-auto w-[168px] object-contain object-left"
        />
      </div>

      <nav className="flex flex-col gap-1 overflow-y-auto px-3 pb-4">
        {visibleNav.map(({ group, path, label, icon: Icon }, index) => {
          const active = path === '/' ? location.pathname === path : location.pathname === path || location.pathname.startsWith(`${path}/`);
          return (
            <div key={path}>
              {(index === 0 || visibleNav[index - 1].group !== group) && <p className={cn('px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground', index > 0 && 'pt-5')}>{group}</p>}
              <button
                onClick={() => { navigate(path); onClose(); }}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
                  active
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
                )}
              >
                <Icon className={cn('h-[18px] w-[18px]', active && 'text-primary')} />
                <span className="flex-1 text-left">{label}</span>
                {active && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
              </button>
            </div>
          );
        })}
      </nav>

      <div className="mt-auto border-t border-sidebar-border p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-sm font-semibold text-accent-foreground">
            PO
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-medium text-sidebar-foreground">Оператор платформы</p>
            <p className="truncate text-xs text-muted-foreground">platform_operator</p>
          </div>
          <button
            onClick={logout}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
            aria-label="Выйти"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
      </aside>
    </>
  );
}
